import React, { useState, useEffect, Fragment } from "react";
import ApexChart from "components/ApexChart/ApexChart";
import "./NewDashBoard.css";
import RewardCard from "components/RewardCard/RewardCard";
import { IoIosTrendingUp, IoIosTrendingDown } from "react-icons/io";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Switch, Tooltip } from "antd";
import FullPageLoader from "components/FullPageLoader/FullPageLoader";

const boxBorderColors = ["#3b4d61", "#1e847f", "#3b4d61", "#1e847f"];
const NewDashBoard = ({apiData, isLoading}) => {
  const [chartToggles, setChartToggles] = useState([]); 
  const [cacheData, setCacheData] = useState(null);

  const chartStyleConfig = {
    className: "chart-item",
    height: "100%",
    width: "100%",
    axisShow: true,
    axisType: null,
    showGrid: true,
    strokeConfig: {
      curve: 'smooth',
      width: 4,
    },
  };


  useEffect(() => {
    // Check if apiData is available and process it safely
    if (apiData) {
      // Process apiData to ensure it's in a safe format
      const errorHandlingData = {
        rewardCounts: apiData.rewardCounts || [],
        totalCounts: apiData.totalCounts || [],
        categoryCounts: apiData.categoryCounts || { data: [] },
        lastUpdated: apiData.lastUpdated || null,
        permissions: apiData.permissions || {},
        lastTwoMonthsCounts: apiData.lastTwoMonthsCounts || { data: [] },
      };

      setCacheData(errorHandlingData);

      if (errorHandlingData.categoryCounts?.data) {
        const initialToggles = errorHandlingData.categoryCounts.data.map(() => true);
        setChartToggles(initialToggles);
      }
    }
  }, [apiData]);

  const chartBox = ({
    type,
    series,
    categories,
    styleConfig,
    color,
    labels = [],
    showLegend = true,
    name,
    isToggle,
    onToggleChange,
    toggleState,
    info,
  }) => {
    return (
      <div className={styleConfig.className || ""} style={{ position: 'relative' }}>
        {isToggle && (
          <div className="dashboard-toggle" >
            <Switch
              checked={toggleState}
              onChange={onToggleChange}
              checkedChildren="Top"
              unCheckedChildren="Low"
              size="medium"
              style={{
                position: 'absolute',
                right: '3rem',
                top: '0.5rem',
                zIndex: 1,
              }}
            />
           { info && <Tooltip title={info} placement="top">
              <AiOutlineInfoCircle style={{
                position: 'absolute',
                right: '0.5rem',
                top: '0.5rem',
                zIndex: 1,
              }} size={21} color="#D35400" />
            </Tooltip>
  }
          </div>
        )}
        <ApexChart
          type={type}
          series={series}
          categories={categories}
          labels={type === "pie" || type === "donut" ? labels : undefined}
          height={styleConfig.height}
          width={styleConfig.width}
          colors={color}
          strokeConfig={styleConfig.strokeConfig}
          axisConfig={{
            show: styleConfig.axisShow,
            axisType: styleConfig.axisType,
            showGrid: styleConfig.showGrid,
            showXaxis: true,
            showYaxis: true,
          }}
          legendConfig={{ show: showLegend }}
          name={name}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const handleToggle = (index) => {
    setChartToggles(prev => {
      const newToggles = [...prev];
      newToggles[index] = !newToggles[index];
      return newToggles;
    });
  };

  const renderCategoryCharts = () => {
    const chartTypes = ["bar", "pie", "bar", "donut"];
    return (
      cacheData.categoryCounts &&
      cacheData.categoryCounts.data.map((category, index) => {
        if(!cacheData.permissions[category.name]){
          return null;
        };
        const chartType = chartTypes[index % chartTypes.length];
        const categoryType = chartToggles[index] ? "top" : "low";
        const info = category[categoryType].info;
        let series = [];
        let categories = [];

        if (chartType === "bar" || chartType === "column") {
          category[categoryType].data.forEach((item) => {
            const [label] = Object.keys(item);
            const [value] = Object.values(item);
            categories.push(label);
            series.push(parseFloat(value));
          });
          series = [{ name: "Categories", data: series }];
        } else if (chartType === "pie" || chartType === "donut") {
          category[categoryType].data.forEach((item) => {
            const [label] = Object.keys(item);
            const [value] = Object.values(item);
            categories.push(label);
            series.push(parseFloat(value));
          });
        } else {
          return null;
        }
        return (
          <Fragment key={category.name}>
            {categories.length > 0 && chartBox({
              type: chartType,
              series: series,
              categories: categories,
              labels: chartType === "pie" || chartType === "donut" ? categories : undefined,
              styleConfig: chartStyleConfig,
              color: ['#5ca0e6', '#B6A6E9', '#F8AE54', '#87BB62'],
              name: category.name,
              isToggle: true,
              onToggleChange: () => handleToggle(index),
              toggleState: chartToggles[index],
              showLegend: false,
              info,
            })}
          </Fragment>
        );
      })
    );
  };


  if(!cacheData || isLoading){
    return <FullPageLoader />
  }
  return (
    <div className="dashboard-container">
      <div className="title-container">
        <h4>Dashboard</h4>
        <div className="reward-cards">
          {cacheData.rewardCounts && cacheData.rewardCounts.map((el) => (
            <div key={el.type}>
              <RewardCard
                name={el.type}
                count={el.qty}
                attribute={el.name}
                info={el.info}
                icon={el.icon}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="section-card count-border">
        <div className="count-box">
          {cacheData.totalCounts?.length > 0 && cacheData.totalCounts.map((transaction, index) => {
            if(!cacheData.permissions[transaction.name]){
              return null;
            };
            return (
            <div className="count-items" key={transaction.name} style={{ borderBottom: `0.31rem solid ${boxBorderColors[index]}` }}>
              <div className="count-title">
                {transaction.name}
                <div className="trend" style={{ color: transaction.trendCount.trend === "increment" ? "#1e847f" : "#D35400" }}>
                  {transaction.trendCount.percent}
                  {transaction.trendCount.trend === "increment" ? <IoIosTrendingUp size={"2rem"} color="#1e847f" /> : <IoIosTrendingDown size={"2rem"} color="#D35400" />}
                </div>
                <div>
                  <Tooltip title={transaction.trendCount.info} placement="top">
                    <AiOutlineInfoCircle style={{ cursor: 'pointer', color: "black" }} />
                  </Tooltip>
                </div>
              </div>
              <div className="count-block">
                <div className="total-count">{transaction.count.split("(")[0].length > 11 ? <Tooltip title={transaction.count} placement="top"> {`${transaction.count.split("(")[0].slice(0, 10).trim()}... (${transaction.count.split("(")[1]}`} </Tooltip> : transaction.count}</div>
                <div className="icon">
                  <img style={{ width: '3.5rem', height: '3.5rem' }} src={transaction.icon} alt={`${transaction.name} icon`} />
                </div>
              </div>
            </div>
          )
          })}
        </div>
        {cacheData.lastUpdated && <div className="last-updated">Last updated: <span>{cacheData.lastUpdated}</span></div>}
      </div>
      <div className="section-card">
        {cacheData.lastTwoMonthsCounts?.headerName && <div className="section-title">{cacheData.lastTwoMonthsCounts.headerName}</div>}
        <div className="chart-container">
          {cacheData.lastTwoMonthsCounts?.data?.map(chart =>{
            if(!cacheData.permissions[chart.name]){
              return null;
            };
            return chartBox({
              type: chart.type,
              series: [
                { name: chart.monthNames[0], data: chart.currentMonth },
                { name: chart.monthNames[1], data: chart.previousMonth },
              ],
              categories: chart.weeks,
              styleConfig: chartStyleConfig,
              color: null,
              name: chart.name,
              isToggle: false,
            })
          }
          )}
        </div>
      </div>
      <div className="section-card">
        {cacheData.categoryCounts?.headerName && <div className="section-title">{cacheData.categoryCounts.headerName}</div>}
        <div className="chart-container-2">
          {renderCategoryCharts()}
        </div>
      </div>
    </div>
  );
};

export default NewDashBoard;
